import { useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useFormContext } from 'react-hook-form'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { MappingType } from '../../../../../../../models/Account'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { useGetSupportedObjectsQuery, useLazyGetDescribeQuery } from '../../../../../../../store/integrations/integrations'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../../../store/hooks'
import { type FormFieldDefinition, useGetIntegrationAdminSettings } from '../../../hooks'
import { IntegrationSettings } from '../../../schema'

const DEFAULT_CALENDAR_OPTION_ID = 'default'
const CUSTOM_CALENDAR_OPTION_ID = 'customEventObject'

interface SelectCalendarTypeSectionProps {
  onChange: () => void
  serviceId: IntegrationServiceId
  disabled?: boolean
}

export const SelectCalendarTypeSection: React.FC<SelectCalendarTypeSectionProps> = ({ onChange, serviceId, disabled }) => {
  const {
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()
  const accountId = useAppSelector(selectAccountId)

  const currentObjectName = watch('mappings.calendar.objectName')
  const currentMappingType = watch('mappings.calendar.mappingType')

  const isCustom = useMemo(() => currentMappingType === MappingType.CUSTOM, [currentMappingType])

  // Static list: no predefined calendar template exists, so a template query would only ever return the custom entry.
  const calendarTypeOptions: SelectOption[] = useMemo(
    () => [
      { id: DEFAULT_CALENDAR_OPTION_ID, label: `${getIntegrationDisplayName(serviceId)} Event (default)` },
      { id: CUSTOM_CALENDAR_OPTION_ID, label: 'Custom event object' },
    ],
    [serviceId],
  )

  const { hasIntegration } = useGetIntegrationAdminSettings(serviceId)

  const { data: supportedObjects, isLoading: isLoadingSupportedObjects } = useGetSupportedObjectsQuery(
    { accountId, connector: serviceId },
    { skip: !accountId || !serviceId || !hasIntegration },
  )

  const selectedCalendarTypeOption = useMemo(
    () => calendarTypeOptions.find((option) => option.id === (isCustom ? CUSTOM_CALENDAR_OPTION_ID : DEFAULT_CALENDAR_OPTION_ID)) ?? null,
    [calendarTypeOptions, isCustom],
  )

  const eventObjectOptions: SelectOption[] = useMemo(
    () => supportedObjects?.map((object) => ({ id: object.name, label: `${object.label} (${object.name})` })) ?? [],
    [supportedObjects],
  )

  const selectedEventObjectOption = useMemo(
    () => eventObjectOptions.find((option) => option.id === currentObjectName) ?? null,
    [eventObjectOptions, currentObjectName],
  )

  const [getDescribe, { isFetching: isCheckingEventObject }] = useLazyGetDescribeQuery()
  const [ownerlessEventObjectLabel, setOwnerlessEventObjectLabel] = useState<string | null>(null)
  const [eventObjectPickerKey, setEventObjectPickerKey] = useState(0)

  /**
   * Validates the object name alone. `shouldValidate` on `mappings.calendar` would write every nested issue too, flagging
   * each required event field and the contact linking before the admin has had a chance to map them. The trigger still
   * recomputes `isValid` for the whole form, so the save bar keeps blocking an incomplete mapping.
   */
  const setCustomCalendarMapping = (objectName: string) => {
    setValue('mappings.calendar', { objectName, mappingType: MappingType.CUSTOM }, { shouldDirty: true })
    clearErrors('mappings.calendar')
    void trigger('mappings.calendar.objectName')
    onChange()
  }

  const handleCalendarTypeSelected = (option: SelectOption) => {
    setOwnerlessEventObjectLabel(null)
    if (option.id === CUSTOM_CALENDAR_OPTION_ID) {
      if (isCustom) {
        return
      }
      setCustomCalendarMapping('')
      return
    }
    if (!isCustom) {
      return
    }
    setValue('mappings.calendar', null, { shouldDirty: true, shouldValidate: true })
    onChange()
  }

  const handleEventObjectSelected = async (option: SelectOption) => {
    if (option.id === currentObjectName) {
      return
    }
    setOwnerlessEventObjectLabel(null)
    const hasOwner = await getDescribe({ accountId, connector: serviceId, context: { objectName: option.id } }, true)
      .unwrap()
      .then((describe) => ((describe.data?.describe?.formFieldDefinitions ?? []) as FormFieldDefinition[]).some((field) => field.name === 'OwnerId'))
      .catch(() => true)
    if (!hasOwner) {
      setOwnerlessEventObjectLabel(option.label)
      setEventObjectPickerKey((previousKey) => previousKey + 1)
      return
    }
    setCustomCalendarMapping(option.id)
  }

  const objectNameError = ownerlessEventObjectLabel
    ? `${ownerlessEventObjectLabel} records have no owner. Choose an object with an Owner field so Zocks knows which user each event belongs to.`
    : errors.mappings?.calendar?.objectName?.message

  return (
    <>
      <Row className='mb-2'>
        <Col>
          <h4>{getIntegrationDisplayName(serviceId)} event object*</h4>
          <p className='zocks-text-gray-600'>Select which {getIntegrationDisplayName(serviceId)} object Zocks reads calendar events from.</p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <SelectDropdown
            initialSelectedOption={selectedCalendarTypeOption}
            options={calendarTypeOptions}
            onOptionSelected={handleCalendarTypeSelected}
            disabled={disabled}
          />
        </Col>
        {isCustom ? (
          <Col md={6}>
            <SelectDropdown
              key={eventObjectPickerKey}
              options={eventObjectOptions}
              initialSelectedOption={selectedEventObjectOption}
              placeholder='Select Object Type'
              onOptionSelected={handleEventObjectSelected}
              searchable
              loading={isLoadingSupportedObjects || isCheckingEventObject}
              disabled={disabled || isCheckingEventObject}
            />
            {objectNameError ? <div className='text-danger mt-1 small'>{objectNameError}</div> : null}
          </Col>
        ) : null}
      </Row>
    </>
  )
}
